// The Distribution step (7.6F.2): choose the platform → the format → see
// honest compatibility → create the Distribution and its permanent Tag. The
// platform is chosen HERE, never when the creative is created, and cannot be
// changed afterwards (another platform = another Distribution). The server
// assesses compatibility; an UNSUPPORTED scope cannot be submitted (no
// override), UNVERIFIED continues with an honest warning.
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode, type SyntheticEvent } from "react";
import { api, call } from "@/api/client";
import { ApiProblem } from "@/api/errors";
import type { CreativeProduct, Distribution } from "@/api/types";
import { COMMON_FORMATS, displayFormat } from "@/lib/labels";
import { Button } from "@/ui/button";
import { Field } from "@/ui/field";
import { Input, NativeSelect } from "@/ui/input";
import { CompatibilitySummary } from "../platforms/compatibility-summary";
import { PlatformPicker, type SelectedPlatform } from "../platforms/platform-picker";
import { useCompatibility } from "../platforms/queries";

const FORMAT_SHAPE = /^[A-Za-z0-9_.:x-]{1,64}$/;

/**
 * Formats of a version: those designed in its Creative Document (v2), in
 * document order; for a not-yet-designed v1 draft, the wizard's format hint.
 */
export function useVersionFormats(orgId: string, versionId: string | undefined) {
  return useQuery({
    queryKey: ["org", orgId, "version", versionId, "formats"],
    queryFn: async () => {
      const v = await call(() =>
        api.GET("/orgs/{orgId}/versions/{versionId}", { params: { path: { orgId, versionId: versionId ?? "" } } })
      );
      if (v.schemaVersion !== 2) {
        const hint = (v.content as { metadata?: { initialFormat?: unknown } }).metadata?.initialFormat;
        return typeof hint === "string" && /^\d{1,4}x\d{1,4}$/.test(hint) ? [hint] : [];
      }
      const layouts = (v.content as { layouts?: Array<{ formatKeys?: unknown }> }).layouts ?? [];
      return [...new Set(layouts.flatMap((l) => (Array.isArray(l.formatKeys) ? (l.formatKeys as string[]) : [])))];
    },
    enabled: Boolean(versionId),
    staleTime: 60_000,
  });
}

export function DistributionForm({
  orgId,
  creativeId,
  product,
  formats,
  canAddPlatform,
  submitLabel,
  pendingLabel,
  onCreated,
  before,
  after,
  footer,
}: {
  orgId: string;
  creativeId: string;
  product: CreativeProduct;
  /** The creative's own formats (from its document); common sizes otherwise. */
  formats: readonly string[];
  canAddPlatform: boolean;
  submitLabel: string;
  pendingLabel?: string;
  onCreated: (distribution: Distribution) => Promise<void> | void;
  before?: ReactNode;
  after?: ReactNode;
  footer: (submit: ReactNode) => ReactNode;
}) {
  const options = formats.length > 0 ? formats : COMMON_FORMATS;
  const [platform, setPlatform] = useState<SelectedPlatform | null>(null);
  const [format, setFormat] = useState<string>(options[0] ?? "300x250");
  const [customFormat, setCustomFormat] = useState("");
  const [name, setName] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const effectiveFormat = format === "other" ? customFormat.trim() : format;
  const validFormat = FORMAT_SHAPE.test(effectiveFormat);
  const compatibility = useCompatibility(orgId, platform, product, validFormat ? effectiveFormat : null);
  const assessment = platform && compatibility.data?.platform.id === platform.id ? compatibility.data : undefined;
  const blocked = assessment?.allowed === false;

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    const fieldErrors: Record<string, string> = {};
    if (!platform) fieldErrors.platform = "Elige dónde vas a utilizar el Tag.";
    if (!validFormat) fieldErrors.format = "Indica un formato como 300x250.";
    setErrors(fieldErrors);
    if (!platform || !validFormat || blocked) return;
    setPending(true);
    try {
      const created = await call(() =>
        api.POST("/orgs/{orgId}/creatives/{creativeId}/distributions", {
          params: { path: { orgId, creativeId } },
          body: {
            name: name.trim() || `${platform.name} ${effectiveFormat}`,
            ...(platform.kind === "custom" ? { customPlatformId: platform.id } : { platformId: platform.id }),
            format: effectiveFormat,
          },
        })
      );
      await onCreated(created);
    } catch (problem) {
      setErrors(
        problem instanceof ApiProblem
          ? { ...problem.fieldErrors(), form: problem.message }
          : { form: "No se pudo crear la distribución." }
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <form className="grid gap-4" onSubmit={(e) => void submit(e)} noValidate>
      {before}
      <PlatformPicker
        orgId={orgId}
        product={product}
        value={platform}
        onChange={(p) => {
          setPlatform(p);
          setErrors((e) => ({ ...e, platform: "" }));
        }}
        canAdd={canAddPlatform}
        error={errors.platform || undefined}
      />
      {platform ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Formato" error={errors.format} hint="El tamaño del anuncio en la plataforma.">
              <NativeSelect
                value={format}
                onChange={(e) => {
                  setFormat(e.target.value);
                }}
              >
                {options.map((f) => (
                  <option key={f} value={f}>
                    {displayFormat(f)}
                  </option>
                ))}
                <option value="other">Otro…</option>
              </NativeSelect>
            </Field>
            {format === "other" ? (
              <Field label="Formato personalizado" hint="Ancho x alto, por ejemplo 300x250.">
                <Input
                  value={customFormat}
                  onChange={(e) => {
                    setCustomFormat(e.target.value);
                  }}
                  maxLength={64}
                />
              </Field>
            ) : null}
          </div>
          {assessment ? (
            <CompatibilitySummary orgId={orgId} assessment={assessment} canRequestReview />
          ) : compatibility.isError ? (
            <p className="text-sm text-danger">No pudimos consultar la compatibilidad. Inténtalo de nuevo.</p>
          ) : validFormat ? (
            <p className="text-sm text-muted" aria-live="polite">
              Consultando compatibilidad…
            </p>
          ) : null}
          <Field label="Nombre de la distribución (opcional)" error={errors.name}>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              maxLength={200}
              placeholder={`${platform.name} ${validFormat ? effectiveFormat : ""}`.trim()}
            />
          </Field>
        </>
      ) : null}
      {after}
      {errors.form ? (
        <p className="text-sm text-danger" role="alert">
          {errors.form}
        </p>
      ) : null}
      {footer(
        <Button type="submit" variant="primary" disabled={pending || blocked}>
          {pending && pendingLabel ? pendingLabel : submitLabel}
        </Button>
      )}
    </form>
  );
}
